import React, { useEffect, useRef, useState } from "react";
import WizardPageWrapper from "../WizardPageWrapper";
import {
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { getStateList } from "../../../../../../store/actions/adminActionCreators";
import { CoverWizardForm, FlowType, StateCode } from "../../CoverWizardModal";
import { poolOwnerInfoPageContainsEmptyFields } from "../../validation-utils";
import ConfirmationModal from "../../../../../../components/ConfirmationModal";
import LeaveWizardConfirmationModal from "../LeaveWizardConfirmationModal";
import useGetFeatureFlagStatuses from "../../../../../../hooks/useGetFeatureFlagStatuses";
import { ZipCodeInputWithAutofill } from "./ShippingInformationPage";

type JobInfoPageProps = {
  usStateOptions: Array<StateCode>;
  hideBackButton: boolean;
  onNextButtonClick: () => void;
  onBackButtonClick?: () => void;
  quoteIsRGA?: boolean;
  hideQuoteType?: boolean;
  hideReturnTypes?: boolean;
};
const quoteTypes: Array<string> = ["Estimate", "Order"];

const dateFormat = "MM/DD/YYYY";
const { Option } = Select;

const otherReturnTypeCheckboxes = [
  { label: "Replacement", value: "replacement" },
  { label: "Repair", value: "repair" },
];

const alterationCheckbox = { label: "Alteration", value: "alteration" };

export default function JobInfoPage({
  onNextButtonClick,
  onBackButtonClick,
  usStateOptions,
  hideBackButton,
  quoteIsRGA = false,
  hideQuoteType = false,
  hideReturnTypes = false,
}: JobInfoPageProps) {
  const [unpopulatedFieldsModalIsOpen, setUnpopulatedFieldsModalIsOpen] =
    useState<boolean>(false);
  const [leaveWizardModalIsOpen, setLeaveWizardModalIsOpen] =
    useState<boolean>(false);
  const closeNavigateToPage2WarningAndProceed = () => {
    if (unpopulatedFieldsModalIsOpen) {
      setUnpopulatedFieldsModalIsOpen(false);
    }
    onNextButtonClick();
  };

  const form = Form.useFormInstance<CoverWizardForm>();

  function attemptNavigateToPage2() {
    form
      .validateFields()
      .then((values) => {
        if (poolOwnerInfoPageContainsEmptyFields(form)) {
          setUnpopulatedFieldsModalIsOpen(true);
          return;
        }
        closeNavigateToPage2WarningAndProceed();
      })
      .catch((errorInfo) => {
        return;
      });
  }

  const poNumber = Form.useWatch("poNumber", form);
  const jobName = Form.useWatch("jobName", form);
  const quoteType = Form.useWatch("quoteType", form);
  const returnTypes = Form.useWatch("returnTypes", form);
  const zip = Form.useWatch("zip", { form, preserve: true });
  const city = Form.useWatch("city", form);
  const stateCode = Form.useWatch("stateCode", form);

  const summaryRef = useRef<HTMLDivElement>(null);

  const { alterationCheckbox: alterationCheckboxEnabled } =
    useGetFeatureFlagStatuses();

  const returnTypeNeedsValue = quoteIsRGA && !hideReturnTypes ? !returnTypes?.length : false;

  const returnTypeCheckboxes = alterationCheckboxEnabled
    ? [...otherReturnTypeCheckboxes, alterationCheckbox]
    : otherReturnTypeCheckboxes;

  return (
    <>
      <WizardPageWrapper
        title="Enter Job Information"
        hideBackButton={hideBackButton}
        suppliedSummaryRef={summaryRef}
        summary={
          <Typography.Paragraph
            ref={summaryRef}
            style={{ fontStyle: "italic" }}
          >
            Get the process started in seconds. Just give your job a name,
            upload a scan or photo of the completed{" "}
            <a
              href={`${process.env.PUBLIC_URL}/assets/2024_Meyco_Order_Estimate_Measure_Form.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline" }}
            >
              order form
            </a>
            , and our team will be in touch in 1-2 business days.
          </Typography.Paragraph>
        }
        onBackButtonClick={() => {
          if (!!onBackButtonClick) {
            onBackButtonClick();
          } else {
            setLeaveWizardModalIsOpen(true);
          }
        }}
        onNextButtonClick={attemptNavigateToPage2}
        disableNext={
          (!poNumber && !jobName) ||
          returnTypeNeedsValue ||
          (!quoteType && !hideQuoteType)
        }
      >
        <Row style={{ overflowY: "auto" }} gutter={[10, 10]}>
          <Col xs={24} md={12}>
            <Form.Item<CoverWizardForm>
              style={{ margin: "0" }}
              name="date"
              label="Date"
            >
              <DatePicker
                disabled
                format={dateFormat}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item<CoverWizardForm>
              style={{ margin: "0" }}
              label="PO#"
              name="poNumber"
              rules={[
                {
                  required: !jobName && !poNumber,
                  message: "PO# or Job Name required.",
                },
              ]}
            >
              <Input
                style={{ width: "100%" }}
                placeholder="#####"
                maxLength={40}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item<CoverWizardForm>
              style={{ margin: "0" }}
              name="jobName"
              label="Enter a name for this job"
              rules={[
                {
                  required: !jobName && !poNumber,
                  message: "Job Name or PO# required.",
                },
              ]}
            >
              <Input placeholder="Job Name" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item<CoverWizardForm>
              style={{ margin: "0" }}
              name="address"
              label="Address"
            >
              <Input placeholder="1600 Pennsylvania Avenue" />
            </Form.Item>
          </Col>
          <Col xs={12} md={5}>
            <ZipCodeInputWithAutofill
              value={zip}
              form={form}
              city={city}
              state={stateCode ?? ""}
              stateFieldName="stateCode"
              cityFieldName="city"
              zipFieldName="zip"
            />
          </Col>
          <Col xs={24} md={14}>
            <Form.Item<CoverWizardForm>
              style={{ margin: "0" }}
              name="city"
              label="City"
            >
              <Input placeholder="Washington" />
            </Form.Item>
          </Col>
          <Col xs={12} md={5}>
            <Form.Item<CoverWizardForm>
              style={{ margin: "0" }}
              name="stateCode"
              label="State"
            >
              <Select
                //this id tricks the browser autofill to not autofill this field for state
                id="stt1"
                showSearch
                placeholder="AZ"
                optionFilterProp="children"
              >
                {usStateOptions.map((state: any) => (
                  <Option key={state.id} value={state.id}>
                    {state.id}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {!hideQuoteType && (
            <Col xs={12}>
              <Form.Item<CoverWizardForm>
                style={{ margin: "0", marginTop: "8px" }}
                name="quoteType"
                label="This is an:"
                rules={[
                  {
                    required: true,
                    message: "A selection is required",
                  },
                ]}
              >
                <Radio.Group
                  onChange={(e) => {
                    if (!hideReturnTypes) {
                      if (
                        e.target.value === "Estimate" &&
                        typeof returnTypes === "string"
                      ) {
                        form.setFieldsValue({
                          returnTypes: [],
                        });
                      } else if (
                        e.target.value === "Order" &&
                        Array.isArray(returnTypes)
                      ) {
                        form.setFieldsValue({
                          returnTypes: "",
                        });
                      }
                    }
                  }}
                >
                  {quoteTypes.map((quoteType) => (
                    <Radio value={quoteType} key={quoteType}>
                      {quoteType}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
          )}
          {quoteIsRGA && (
            <>
              <Col xs={24}>
                <Divider style={{ margin: "14px 0" }} />
              </Col>
              {!hideReturnTypes && (
                <Col xs={24}>
                  <Form.Item<CoverWizardForm>
                    style={{ margin: "0" }}
                    name="returnTypes"
                    label="Return Cover to Meyco"
                    rules={[
                      {
                        required: true,
                        message: "Please select an option",
                      },
                    ]}
                  >
                    {quoteType === "Estimate" ? (
                      <Checkbox.Group options={returnTypeCheckboxes} />
                    ) : quoteType === "Order" ? (
                      <Radio.Group options={returnTypeCheckboxes} />
                    ) : null}
                  </Form.Item>
                </Col>
              )}
              <Col xs={24}>
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name="planNumber"
                  label="Meyco Plan #"
                >
                  <Input />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </WizardPageWrapper>
      <ConfirmationModal
        modalIsOpenState={[
          unpopulatedFieldsModalIsOpen,
          setUnpopulatedFieldsModalIsOpen,
        ]}
        headerText="Leave job information unpopulated?"
        bodyText="It helps us give you as accurate a quote as possible. You can always come back and change it later."
        onProceed={closeNavigateToPage2WarningAndProceed}
        proceedButtonText="Continue"
        cancelButtonText="Back"
      />
    </>
  );
}
